import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import { formatAmountZhCn } from "../../utils";
import { Typography } from "@douyinfe/semi-ui";

const { Text } = Typography;

export interface PlanRepaymentOverviewProps {
  summaryRow: any;
  currentMonth: number;
  currentYear: number;
  onMonthClick?: (yearMonth: string) => void;
}

const MONTH_NAMES = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];

export default function PlanRepaymentOverview(props: PlanRepaymentOverviewProps) {
  const { summaryRow, currentMonth, currentYear, onMonthClick } = props;

  const chartData = useMemo(() => {
    if (!summaryRow) return [];
    const months = [];
    for (let i = 0; i < 6; i++) {
      let m = currentMonth + i;
      let y = currentYear;
      if (m > 12) { m -= 12; y += 1; }
      const key = `month${i + 1}Amount`;
      const paidKey = `month${i + 1}PaidAmount`;
      months.push({
        name: `${m}月`,
        yearMonth: `${y}-${String(m).padStart(2, '0')}`,
        待还: Number(summaryRow[key]) || 0,
        已还: Number(summaryRow[paidKey]) || 0,
      });
    }
    return months;
  }, [summaryRow, currentMonth, currentYear]);

  const yearCalendarData = useMemo(() => {
    const months = [];
    for (let m = 1; m <= 12; m++) {
      const idx = m - currentMonth;
      let amount = 0;
      let paid = 0;
      if (idx >= 0 && idx < 6 && summaryRow) {
        const key = `month${idx + 1}Amount`;
        const paidKey = `month${idx + 1}PaidAmount`;
        amount = Number(summaryRow[key]) || 0;
        paid = Number(summaryRow[paidKey]) || 0;
      }
      months.push({
        month: m,
        label: MONTH_NAMES[m - 1],
        yearMonth: `${m >= currentMonth ? currentYear : currentYear + 1}-${String(m).padStart(2, '0')}`,
        amount,
        paid,
        isCurrent: m === currentMonth,
        inRange: idx >= 0 && idx < 6,
      });
    }
    return months;
  }, [summaryRow, currentMonth, currentYear]);

  if (!summaryRow) return null;

  const formatYAxis = (value: number) => {
    if (value >= 10000) return (value / 10000).toFixed(0) + '万';
    return String(value);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* 趋势图 */}
      <div style={{ background: '#fff', borderRadius: 8, padding: '12px 16px', marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Text style={{ fontSize: 14, fontWeight: 600 }}>未来6月资金趋势</Text>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} barGap={4} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} width={50} />
            <Tooltip
              formatter={(value: number, name: string) => [formatAmountZhCn(value), name]}
              contentStyle={{ borderRadius: 6, fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="待还" fill="#e91e63" radius={[3, 3, 0, 0]} barSize={24}
              onClick={(data) => onMonthClick?.(data.yearMonth)} cursor={onMonthClick ? "pointer" : "default"} />
            <Bar dataKey="已还" fill="#00b578" radius={[3, 3, 0, 0]} barSize={24}
              onClick={(data) => onMonthClick?.(data.yearMonth)} cursor={onMonthClick ? "pointer" : "default"} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* 年度日历视图 */}
      <div style={{ background: '#fff', borderRadius: 8, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Text style={{ fontSize: 14, fontWeight: 600 }}>{currentYear}年月度资金日历</Text>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginTop: 8 }}>
          {yearCalendarData.map((item) => (
            <div
              key={item.month}
              onClick={() => item.inRange && onMonthClick?.(item.yearMonth)}
              style={{
                background: item.isCurrent ? '#e6f7ff' : item.inRange ? (item.amount > 0 ? '#fff1f0' : '#f6ffed') : '#fafafa',
                border: item.isCurrent ? '2px solid #1677ff' : '1px solid #f0f0f0',
                borderRadius: 6,
                padding: '8px 10px',
                cursor: item.inRange ? 'pointer' : 'default',
                opacity: item.inRange ? 1 : 0.5,
                transition: 'transform 0.15s',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: item.isCurrent ? 700 : 400, color: item.isCurrent ? '#1677ff' : '#333' }}>
                {item.label}
                {item.isCurrent && <span style={{ fontSize: 10, marginLeft: 4, color: '#1677ff' }}>当月</span>}
              </div>
              {item.inRange && item.amount > 0 && (
                <div style={{ fontSize: 11, color: '#e91e63', fontWeight: 600, marginTop: 2 }}>
                  待还 {formatAmountZhCn(item.amount)}
                </div>
              )}
              {item.inRange && item.paid > 0 && (
                <div style={{ fontSize: 11, color: '#00b578', marginTop: 1 }}>
                  已还 {formatAmountZhCn(item.paid)}
                </div>
              )}
              {item.inRange && item.amount === 0 && item.paid === 0 && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>--</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
